import { TrackingEventName } from '@/models/analytics/eventTypes';
import { createExperimentProps } from '@/utils/experimentNameConverter';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import { track } from '@/lib/analytics/track';
import {
  confirmEvidenceSBSCandidateAction,
  evidenceSBSDisplayedTimeAtom,
  evidenceSBSExperimentModeAtom,
} from '../atoms';
import { useConfirmEvidenceSBSMutation } from '../mutations/useConfirmEvidenceSBSMutation';
import { EvidenceSBSStreamStatus, type EvidenceSBSCandidate } from '../types';
import type { SBSStreamState } from './useEvidenceSBSStreams';

const TERMINAL_STATUSES: EvidenceSBSStreamStatus[] = [
  EvidenceSBSStreamStatus.DONE,
  EvidenceSBSStreamStatus.ERROR,
];

export interface UseEvidenceSBSComparisonArgs {
  chatId: string;
  candidates: EvidenceSBSCandidate[];
  streamA: SBSStreamState;
  streamB: SBSStreamState;
  onConfirmed?: () => void;
  onMutationSuccess?: (chosenCandidateId?: string) => void;
  onSkip?: () => void;
}

export interface UseEvidenceSBSComparisonResult {
  handleConfirm: (candidate: EvidenceSBSCandidate) => void;
  handleSkip: () => void;
  isPending: boolean;
  /**
   * EVI-1766: true once BOTH streams have reached a terminal status
   * (DONE/ERROR). The comparison disables the "Prefer this response" buttons
   * until then so users can't pick before reading complete answers — only
   * Skip stays available while either stream is still active.
   */
  allDoneOrErrored: boolean;
}

/**
 * EVI-1680: extracted controller for {@link EvidenceSBSComparison} so the
 * component file stays presentational (and under the `max-lines` budget).
 * Owns the confirm/skip mutations, analytics, and the auto-fallback /
 * experiment-tracking effects.
 */
export function useEvidenceSBSComparison({
  chatId,
  candidates,
  streamA,
  streamB,
  onConfirmed,
  onMutationSuccess,
  onSkip,
}: UseEvidenceSBSComparisonArgs): UseEvidenceSBSComparisonResult {
  const displayedTime = useAtomValue(evidenceSBSDisplayedTimeAtom);
  const setDisplayedTime = useSetAtom(evidenceSBSDisplayedTimeAtom);
  const experimentMode = useAtomValue(evidenceSBSExperimentModeAtom);
  const confirmCandidate = useSetAtom(confirmEvidenceSBSCandidateAction);
  const { mutate: confirmMutate, isPending } = useConfirmEvidenceSBSMutation();

  const allDoneOrErrored =
    TERMINAL_STATUSES.includes(streamA.status) &&
    TERMINAL_STATUSES.includes(streamB.status);
  const aOk = streamA.status === EvidenceSBSStreamStatus.DONE;
  const bOk = streamB.status === EvidenceSBSStreamStatus.DONE;

  // Auto-fallback: when exactly one candidate succeeds, confirm it automatically.
  // When both fail, dismiss the comparison.
  const autoFallbackRef = useRef(false);
  useEffect(() => {
    if (!allDoneOrErrored || autoFallbackRef.current) return;
    if (aOk && bOk) return; // Both succeeded — normal comparison flow

    autoFallbackRef.current = true;

    if (aOk || bOk) {
      const winner = aOk ? candidates[0]! : candidates[1]!;
      confirmCandidate(winner.candidate_id);

      track({
        event_name: TrackingEventName.ExperimentStarted,
        props: {
          ...createExperimentProps(experimentMode),
          evidence_sbs_action: 'auto_fallback',
          evidence_sbs_chat_id: chatId,
          evidence_sbs_selected_candidate_id: winner.candidate_id,
          evidence_sbs_selected_alternative: !winner.is_default,
        } as never,
        options: { amplitude: false, posthog: true },
      });

      const now = new Date();
      const winnerId = winner.candidate_id;
      confirmMutate(
        {
          reqBody: {
            chat_id: chatId,
            selected_candidate_id: winnerId,
            displayed_at: (displayedTime ?? now).toISOString(),
            selected_at: now.toISOString(),
          },
        },
        {
          onSuccess: () => onMutationSuccess?.(winnerId),
        }
      );

      onConfirmed?.();
    } else {
      // Both failed — dismiss and let the chat refetch. No winning candidate
      // to sync, so onMutationSuccess gets undefined (parent will skip the
      // stream→chat handoff).
      onConfirmed?.();
      onMutationSuccess?.(undefined);
    }
  }, [
    allDoneOrErrored,
    aOk,
    bOk,
    candidates,
    chatId,
    confirmCandidate,
    confirmMutate,
    displayedTime,
    experimentMode,
    onConfirmed,
    onMutationSuccess,
  ]);

  useEffect(() => {
    if (candidates.length >= 2 && !displayedTime) {
      setDisplayedTime(new Date());
    }
  }, [candidates.length, displayedTime, setDisplayedTime]);

  const experimentTrackedRef = useRef(false);
  useEffect(() => {
    if (candidates.length >= 2 && experimentMode && !experimentTrackedRef.current) {
      experimentTrackedRef.current = true;
      track({
        event_name: TrackingEventName.ExperimentStarted,
        props: createExperimentProps(experimentMode),
        options: { amplitude: false, posthog: true },
      });
    }
  }, [candidates, experimentMode]);

  const handleConfirm = useCallback(
    (candidate: EvidenceSBSCandidate) => {
      // Claim the auto-fallback ref synchronously so a stream flipping to a
      // terminal status while the confirm is in flight doesn't trigger a
      // second confirmMutate from the auto-fallback effect above.
      autoFallbackRef.current = true;

      confirmCandidate(candidate.candidate_id);

      track({
        event_name: TrackingEventName.ExperimentStarted,
        props: {
          ...createExperimentProps(experimentMode),
          evidence_sbs_action: 'candidate_selected',
          evidence_sbs_chat_id: chatId,
          evidence_sbs_selected_candidate_id: candidate.candidate_id,
          evidence_sbs_selected_alternative: !candidate.is_default,
        } as never,
        options: { amplitude: false, posthog: true },
      });

      // EVI-1766: the comparison only enables "Prefer this response" once
      // BOTH streams are terminal, so by the time we reach here both answers
      // are fully streamed — commit the pick immediately. (Mid-stream
      // selection / the wait-for-stream hand-off was removed in EVI-1766.)
      const now = new Date();
      confirmMutate(
        {
          reqBody: {
            chat_id: chatId,
            selected_candidate_id: candidate.candidate_id,
            displayed_at: (displayedTime ?? now).toISOString(),
            selected_at: now.toISOString(),
          },
        },
        {
          onSuccess: () => onMutationSuccess?.(candidate.candidate_id),
        }
      );

      onConfirmed?.();
    },
    [
      chatId,
      experimentMode,
      confirmCandidate,
      confirmMutate,
      displayedTime,
      onConfirmed,
      onMutationSuccess,
    ]
  );

  const handleSkip = useCallback(() => {
    track({
      event_name: TrackingEventName.ExperimentStarted,
      props: {
        ...createExperimentProps(experimentMode),
        evidence_sbs_action: 'selection_skipped',
        evidence_sbs_chat_id: chatId,
      } as never,
      options: { amplitude: false, posthog: true },
    });

    // Always defer to the overlay's skip flow, which waits for the
    // default stream to settle and then syncs its content into
    // chatStateAtom + dismisses. The skip path deliberately does NOT
    // hit `/chat/sbs/confirm` — the default candidate's chat message is
    // already persisted by the streaming completion, and posting a
    // confirm with `selected_candidate_id = default` would corrupt the
    // SBS win-rate measurement (Skip ≠ "user picked default"). See the
    // skip useEffect in EvidenceSBSOverlay.
    onSkip?.();
  }, [onSkip, experimentMode, chatId]);

  return { handleConfirm, handleSkip, isPending, allDoneOrErrored };
}
